import { useEffect, useRef, useState } from "react";
import { ref, get } from "firebase/database";
import { database } from "../../config/firebaseConfig";
import { useRemote } from "../../hooks/useRemote";
import useGlobalStore from "../../store/global.store";

const LS_KEY = "wsAnnounceLastSeen";
const DEFAULT_HEADER = "What's New On Your WaitSign!";

const WSANNTV_STYLE = `
  .wsanntv-root {
    position: fixed; inset: 0; z-index: 99999;
    background: rgba(1, 2, 25, 0.80);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Lato', sans-serif; user-select: none;
  }
  .wsanntv-card {
    position: relative;
    width: 1400px; max-width: 94vw;
    background: linear-gradient(180deg, #6bbdf5 0%, #a5d9fa 55%, #e8f6ff 100%);
    border-radius: 30px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    padding: 48px 54px 0;
    display: flex; align-items: flex-end; gap: 36px;
    overflow: hidden;
  }
  .wsanntv-cast {
    height: 600px; width: auto; flex-shrink: 0;
    display: block;
  }
  .wsanntv-right {
    flex: 1; display: flex; flex-direction: column;
    align-items: stretch; gap: 26px; padding-bottom: 38px;
  }
  .wsanntv-header {
    font-family: 'Lato', sans-serif;
    font-weight: 900; font-size: 44px;
    color: #1a2542; letter-spacing: 0.5px;
    line-height: 1.1;
  }
  .wsanntv-bubble {
    position: relative;
    background: #ffffff;
    border: 4px solid #1a2542;
    border-radius: 22px;
    padding: 32px 36px;
    min-height: 240px;
    display: flex; flex-direction: column; gap: 14px;
  }
  .wsanntv-bubble::after {
    content: ""; position: absolute; left: -25px; bottom: 58px;
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-right: 21px solid #1a2542;
  }
  .wsanntv-title {
    font-family: 'Lato', sans-serif;
    color: #1a2542; font-weight: 900; font-size: 40px; line-height: 1.15;
  }
  .wsanntv-body {
    font-family: 'Lato', sans-serif;
    color: #33415c; font-weight: 500; font-size: 31px; line-height: 1.4;
    white-space: pre-line;
  }
  .wsanntv-footer {
    display: flex; align-items: center; justify-content: space-between;
  }
  .wsanntv-dots { display: flex; gap: 11px; }
  .wsanntv-dot {
    width: 15px; height: 15px; border-radius: 50%;
    background: rgba(26,37,66,0.25);
  }
  .wsanntv-dot.on { background: #1a2542; }
  .wsanntv-btn {
    background: #007bff; color: #ffffff;
    border: 5px solid transparent; border-radius: 999px;
    padding: 16px 52px;
    font-family: 'Lato', sans-serif; font-weight: 900; font-size: 31px;
    letter-spacing: 0.5px; cursor: pointer; outline: none;
    box-shadow: 0 2px 8px rgba(0, 53, 128, 0.35);
  }
  .wsanntv-btn.tv-focused { border-color: #3ADF3C; }
`;

export default function AnnouncementPopupTV({ device }) {
  const { updateView } = useRemote();
  const [pages, setPages] = useState(null);
  const [header, setHeader] = useState(DEFAULT_HEADER);
  const [idx, setIdx] = useState(0);
  const [btnFocused, setBtnFocused] = useState(false);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!device?._id) return;
    const lastSeen = parseInt(localStorage.getItem(LS_KEY) || "0", 10) || 0;
    get(ref(database, "config/announcements"))
      .then((snap) => {
        const data = snap.exists() ? snap.val() : null;
        if (!data) return;
        const hasBeacon =
          device?.has_beacon_addon === true ||
          useGlobalStore.getState().hasBeaconAddOn === true;
        if (data.skip && device?._id && data.skip[device._id] === true) return;
        if (typeof data.header === "string" && data.header.trim()) {
          setHeader(data.header);
        }
        const list = Object.entries(data)
          .filter(([, v]) => v && typeof v === "object" && (v.title || v.body))
          .map(([id, v]) => ({ id: parseInt(id, 10), ...v }))
          .filter(
            (a) => Number.isFinite(a.id) && a.id > lastSeen && a.active !== false && !(a.hide_for_beacon === true && hasBeacon),
          )
          .sort((a, b) => a.id - b.id);
        if (list.length) setPages(list);
      })
            .catch(() => {});
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?._id]);

  // If the announcement currently shown names a page, switch the screen
  // BEHIND the popup to it, so dismissing leaves the user standing there.
  useEffect(() => {
    if (!pages) return;
    const p = pages[idx];
    if (p && typeof p.page === "string" && p.page.trim()) {
      updateView(p.page.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, idx]);

  // Autofocus the button as soon as the popup exists (PhotoFrame pattern),
  // and re-grab focus if anything ever steals it while we're up.
  useEffect(() => {
    if (!pages) return;
    const t = setTimeout(() => btnRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [pages]);

  if (!pages) return null;

  const lastName = device?.subscription?.last_name || "";
  const fill = (s) =>
    String(s || "")
      .replace(/\{last_name\}/g, lastName)
      .replace(/\\n/g, "\n")
      .replace(/\{device\}/g, "TV")
      .replace(/ {2,}/g, " ");

  const page = pages[idx];
  const isLast = idx === pages.length - 1;

  const advance = () => {
    if (!isLast) {
      setIdx(idx + 1);
      btnRef.current?.focus();
      return;
    }
    const maxId = pages[pages.length - 1].id;
    localStorage.setItem(LS_KEY, String(maxId));
    setPages(null);
  };

  const handleKeyDown = (e) => {
    // This window owns the remote: nothing leaks to the screen behind it.
    e.preventDefault();
    e.stopPropagation();
    if (e.key === "Enter" || e.key === " ") {
      advance();
    }
    // Arrows (and everything else) are deliberately swallowed — there is
    // nowhere else to go.
  };

  return (
    <div className="wsanntv-root" onClick={(e) => e.stopPropagation()}>
      <style>{WSANNTV_STYLE}</style>
      <div className="wsanntv-card">
        <img className="wsanntv-cast" src="/static/castmember-one.png" alt="" />
        <div className="wsanntv-right">
          <div className="wsanntv-header">{fill(typeof page.header === "string" && page.header.trim() ? page.header : header)}</div>
          <div className="wsanntv-bubble">
            <div className="wsanntv-title">{fill(page.title)}</div>
            <div className="wsanntv-body">{fill(page.body)}</div>
          </div>
          <div className="wsanntv-footer">
            <div className="wsanntv-dots">
              {pages.map((p, i) => (
                <div key={p.id} className={`wsanntv-dot ${i === idx ? "on" : ""}`} />
              ))}
            </div>
            <button
              ref={btnRef}
              className={`wsanntv-btn ${btnFocused ? "tv-focused" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                advance();
              }}
              onFocus={() => setBtnFocused(true)}
              onBlur={() => {
                setBtnFocused(false);
                // Single-stop trap: if focus ever escapes, pull it back.
                setTimeout(() => btnRef.current?.focus(), 50);
              }}
              onKeyDown={handleKeyDown}
              tabIndex={0}
            >
              {fill(typeof page.button === "string" && page.button.trim() ? page.button : (isLast ? "Awesome!" : "Next"))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
